// eslint-disable-next-line no-redeclare
const S = {};

S.modules = {
    /**
     * If true, it will observe DOM changes and init new modules
     *
     * @see autoInit
     * @see autoDestroy
     * @var {boolean}
     */
    autoObserve: true,

    /**
     * If true, it will initialize all modules after the document is ready
     *
     * @see autoObserve
     * @var {boolean}
     */
    autoInit: true,

    /**
     * If true, it will destroy all modules on removal from DOM
     *
     * @see autoObserve
     * @var {boolean}
     */
    autoDestroy: true,

    /**
     * Initialize modules as soon as they move into the viewport
     *
     * If 'optin' is used, only modules with the `lazyloadOptions.optinClass` class will be lazyloaded.
     *
     * If 'optout' is used, all modules will be lazyloaded except those with the `lazyloadOptions.optoutClass` class.
     *
     * @var {boolean|string} Can be false, 'optin' or 'optout'
     */
    lazyload: 'optout',

    /**
     * @var {Object}
     */
    lazyloadOptions: {
        optinClass: 'is-lazy',
        optoutClass: 'no-lazy',
        loadedClass: 'is-loaded',

        /**
         * Margin around the viewport
         *
         * @var {string} Accepts shorthand values like a normal marign (top right bottom left)
         */
        margin: '200px',

        /**
         * Indicate at what percentage of the module's visibility the lazyloading should be executed.
         *
         * The default is 0 (meaning as soon as even one pixel is visible). A value of 1.0 means that the threshold
         * isn't considered passed until every pixel is visible.
         *
         * @var {Number} Range between 0 and 1
         */
        threshold: 0,
    },

    /**
     * Array of all initialized modules
     *
     * @var {Object[]}
     */
    instances: [],

    /**
     * Array of all supported HTML tags
     *
     * @var {string[]}
     */
    elements: ['*'],

    /**
     * Array of all supported module prefix classes
     *
     * @var {string[]}
     */
    prefixes: ['mod-', 'nav-'],

    /**
     * @var {MutationObserver}
     * @private
     */
    mutationObserver: null,

    /**
     * @var {IntersectionObserver}
     * @private
     */
    intersectionObserver: null,

    /**
     * Initialize all modules. Optionally only modules for a given context.
     *
     * @param {string|HTMLElement|Node|jQuery} [context]
     * @param {boolean} [includeChildren]
     *
     * @returns {Object[]}
     */
    init(context, includeChildren = true)
    {
        if (typeof context === 'undefined')
        {
            context = document.body;
        }
        else if (context instanceof jQuery)
        {
            context = context.get(0);
        }

        if (!(context instanceof Element))
        {
            return [];
        }

        const
            initModules = [],
            startModules = [],
            selectors = includeChildren ? `:scope ${this.elements.join(', :scope ')}` : ':scope',
            elements = [context, ...context.querySelectorAll(selectors)];

        for (const element of elements)
        {
            for (const attribute of element.attributes)
            {
                if (!attribute || !attribute.name.startsWith('data-js-'))
                {
                    continue;
                }

                if (this.intersectionObserver)
                {
                    this.intersectionObserver.unobserve(element);
                }

                if (element.classList.contains(this.lazyloadOptions.optinClass))
                {
                    if (this.lazyloadOptions.loadedClass)
                    {
                        element.classList.add(this.lazyloadOptions.loadedClass);
                    }
                    else
                    {
                        element.classList.remove(this.lazyloadOptions.optinClass);
                    }
                }

                const
                    $module = jQuery(element),
                    name = this._dashToCamelCase(attribute.name.slice(8));

                if ($module.data('module_' + name))
                {
                    // console.info('S.' + name + ' already initialized!');

                    continue;
                }

                if (typeof S[name] === 'object' && typeof S[name].init === 'function')
                {
                    let parentObject = S[name],
                        originalObject = parentObject;

                    // originalObject does not exist
                    // save originalObject and hook all functions
                    if (typeof originalObject._originalObject === 'undefined')
                    {
                        S[name] = jQuery.extend(true, {}, parentObject);
                        S[name]._originalObject = originalObject;

                        parentObject = S[name];

                        // hook all function calls
                        for (const key in parentObject)
                        {
                            if (!Object.prototype.hasOwnProperty.call(parentObject, key))
                            {
                                continue;
                            }

                            if (key === 'init' || key === 'start' || key === 'update')
                            {
                                continue;
                            }

                            if (typeof parentObject[key] === 'function')
                            {
                                // must stay a "normal" not an arrow function!
                                parentObject[key] = function ()
                                {
                                    for (const instance of parentObject.instances)
                                    {
                                        instance[key].apply(instance, arguments);
                                    }
                                };
                            }
                        }
                    }

                    // originalObject exists - reuse it
                    else
                    {
                        originalObject = originalObject._originalObject;
                    }

                    // keep reference to all instances
                    if (typeof parentObject.instances === 'undefined')
                    {
                        parentObject.instances = [];
                    }

                    const object = jQuery.extend(true, {}, originalObject);

                    object.name = name;
                    object.$module = $module;
                    object.id = this.instances.length;
                    object.instance = parentObject.instances.length;
                    object.config = this._parseConfig(object.config || {}, attribute.value, $module);
                    object.elements = this._findScriptElements(object.elements || {}, element);
                    object.lazy = $module.hasClass(this.lazyloadOptions.optinClass);

                    // ensure jQuery object
                    for (const key in object.elements)
                    {
                        if (!Object.prototype.hasOwnProperty.call(object.elements, key))
                        {
                            continue;
                        }

                        const value = object.elements[key];

                        if (value instanceof jQuery)
                        {
                            continue;
                        }

                        object.elements[key] = jQuery(value);
                    }

                    parentObject.instances.push(object);
                    object.instances = parentObject.instances;

                    this.instances.push(object);

                    // save instance as jQuery data
                    $module.data('has_module', true);
                    $module.data('module_' + name, object);
                    $module.data('modules', ($module.data('modules') || []).concat(object));

                    // initialize relations
                    // this._initRelations(object);

                    object.init(object['$module'], object['config'], object['elements']);

                    initModules.push(object);

                    // has start function
                    if (typeof object.start === 'function')
                    {
                        startModules.push(object);
                    }
                }

                else
                {
                    console.warn('S.' + name + ' could not be initialized.', $module);
                }
            }
        }

        // there is no start function when lazyloading
        if (!this.lazyload)
        {
            // call start functions
            for (const startModule of startModules)
            {
                startModule.start(startModule['$module'], startModule['config'], startModule['elements']);
            }
        }

        return initModules;
    },

    /**
     * Initialize all modules. Optionally only modules for a given context.
     *
     * @param {string|HTMLElement|Node|jQuery} [context]
     *
     * @returns {Object[]}
     */
    initLazy(context)
    {
        'use strict';

        if (typeof IntersectionObserver !== 'function')
        {
            this.lazyload = false;

            return this.init(context);
        }

        if (!this.intersectionObserver)
        {
            this.intersectionObserver = new IntersectionObserver(this._lazy.bind(this), {
                root: this.lazyloadOptions.root || null,
                rootMargin: this.lazyloadOptions.margin,
                threshold: this.lazyloadOptions.threshold,
            });
        }

        if (typeof context === 'undefined')
        {
            context = document.body;
        }
        else if (context instanceof jQuery)
        {
            context = context.get(0);
        }

        if (!(context instanceof Element))
        {
            return [];
        }

        const
            loadedModules = [],
            selectors = `:scope ${this.elements.join(', :scope ')}`,
            elements = [context, ...context.querySelectorAll(selectors)];

        for (const element of elements)
        {
            for (const attribute of element.attributes)
            {
                if (!attribute || !attribute.name.startsWith('data-js-'))
                {
                    continue;
                }

                if (this.lazyload === 'optout')
                {
                    if (element.classList.contains(this.lazyloadOptions.optoutClass))
                    {
                        const modules = this.init(element, false);

                        loadedModules.splice(-1, 0, ...modules);
                    }
                    else
                    {
                        this.intersectionObserver.observe(element);
                    }
                }

                else if (this.lazyload === 'optin')
                {
                    if (element.classList.contains(this.lazyloadOptions.optinClass))
                    {
                        this.intersectionObserver.observe(element);
                    }
                    else
                    {
                        const modules = this.init(element, false);

                        loadedModules.splice(-1, 0, ...modules);
                    }
                }

                break;
            }
        }

        if (loadedModules.length)
        {
            this._emitUpdate(loadedModules, []);
        }

        return loadedModules;
    },

    /**
     * @param {IntersectionObserverEntry[]} entries
     * @private
     */
    _lazy(entries)
    {
        const loadedModules = [];

        for (const entry of entries)
        {
            if (!entry.isIntersecting)
            {
                entry.target.classList.add(this.lazyloadOptions.optinClass);

                continue;
            }

            const modules = this.init(entry.target, false);

            loadedModules.splice(-1, 0, ...modules);
        }

        if (loadedModules.length)
        {
            this._emitUpdate(loadedModules, []);
        }
    },

    /**
     * Initialize the mutation observer
     *
     * @private
     */
    _initObserver()
    {
        'use strict';

        if (this.mutationObserver !== null)
        {
            return;
        }

        if (typeof MutationObserver !== 'function')
        {
            return;
        }

        this.mutationObserver = new MutationObserver(this._observe.bind(this));

        this.mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });
    },

    /**
     * @param {MutationRecord[]} mutations
     * @private
     */
    _observe(mutations)
    {
        'use strict';

        const
            loadedModules = [],
            destroyedModules = [];

        for (const mutation of mutations)
        {
            if (mutation.type !== 'childList')
            {
                continue;
            }

            if (this.autoDestroy)
            {
                for (const removedNode of mutation.removedNodes)
                {
                    // ignore text nodes
                    if (removedNode instanceof Text)
                    {
                        continue;
                    }

                    const modules = this.destroy(removedNode);

                    destroyedModules.splice(-1, 0, ...modules);
                }
            }

            if (this.autoInit)
            {
                for (const addedNode of mutation.addedNodes)
                {
                    // ignore text nodes
                    if (addedNode instanceof Text)
                    {
                        continue;
                    }

                    const modules = this.lazyload ?
                        this.initLazy(addedNode) :
                        this.init(addedNode);

                    loadedModules.splice(-1, 0, ...modules);
                }
            }
        }

        if (loadedModules.length || destroyedModules.length)
        {
            this._emitUpdate(loadedModules, destroyedModules);
        }
    },

    /**
     * Destroy all initialized modules. Optionally only modules for a given context.
     *
     * @param {string|HTMLElement|Node|jQuery} [context]
     *
     * @returns {Object[]}
     */
    destroy(context)
    {
        'use strict';

        if (typeof context === 'undefined')
        {
            context = document.body;
        }
        else if (context instanceof jQuery)
        {
            context = context.get(0);
        }

        if (!(context instanceof Element))
        {
            return [];
        }

        const
            destroyedModules = [],
            selectors = `:scope ${this.elements.join(', :scope ')}`,
            elements = [context, ...context.querySelectorAll(selectors)];

        for (const element of elements)
        {
            for (const attribute of element.attributes)
            {
                if (!attribute || !attribute.name.startsWith('data-js-'))
                {
                    continue;
                }

                const
                    $module = jQuery(element),
                    name = this._dashToCamelCase(attribute.name.slice(8)),
                    module = $module.data('module_' + name);

                if (!module)
                {
                    // console.info('S.' + name + ' is not initialized!');

                    continue;
                }

                this._destroyModule(module, $module);

                destroyedModules.push(module);
            }
        }

        return destroyedModules;
    },

    /**
     * Destroy all initialized but decoupled modules.
     */
    cleanup()
    {
        'use strict';

        for (const module of this.instances)
        {
            if (!module)
            {
                continue;
            }

            const $module = module.$module;

            // module is still in DOM
            if (jQuery.contains(document.documentElement, $module.get(0)))
            {
                continue;
            }

            this._destroyModule(module, $module, true);
        }
    },

    /**
     * Destroy a single module instance.
     *
     * @param {Object} module
     * @param {jQuery} [$module]
     * @param {boolean} [decoupled]
     * @private
     */
    _destroyModule(module, $module = null, decoupled = false)
    {
        if (typeof module.$module === 'object')
        {
            $module = module.$module;
        }

        // remove (global) instance but keep continuous ids
        const globalIndex = this.instances.indexOf(module);

        if (globalIndex !== -1)
        {
            delete this.instances[globalIndex];
        }

        const parentObject = S[module.name];

        // remove (local) instance but keep continuous ids
        const localIndex = parentObject.instances.indexOf(module);

        if (localIndex !== -1)
        {
            delete parentObject.instances[localIndex];
        }

        // remove jQuery data
        if ($module)
        {
            $module.removeData('module_' + module.name);

            const jQueryModules = ($module.data('modules') || []).filter((moduleInstance) =>
            {
                return moduleInstance !== module;
            });

            if (jQueryModules.length)
            {
                $module.data('modules', jQueryModules);
            }
            else
            {
                $module.removeData(['has_module', 'modules']);
            }
        }

        // remove relations
        // this._removeRelations(module);

        // call destroy function
        if (typeof module.destroy === 'function')
        {
            module.destroy(decoupled);
        }
    },

    /**
     * Converts a dash-string to lowerCamelCase.
     *
     * @param {string} value
     * @return {string}
     * @private
     */
    _dashToCamelCase(value)
    {
        'use strict';

        return value.toLowerCase().replace(/-(\w)/g, function (a, b)
        {
            return b.toUpperCase();
        });
    },

    /**
     * Returns all ".js-variable-name" descendants.
     *
     * @param {Object} results
     * @param {HTMLElement} context
     * @returns {Object}
     * @private
     */
    _findScriptElements(results, context)
    {
        'use strict';

        const allScriptElements = [...context.querySelectorAll(':scope [class*="js-"]')];

        // early skip
        if (!allScriptElements.length)
        {
            return results;
        }

        const
            selectors = `:scope [class^="${this.prefixes.join('"] [class*="js-"], :scope [class^="')}"] [class*="js-"]`,
            irrelevantScriptElements = [...context.querySelectorAll(selectors)];

        if (allScriptElements.length <= irrelevantScriptElements.length)
        {
            return results;
        }

        const scriptElements = allScriptElements.filter((x) =>
        {
            return !irrelevantScriptElements.includes(x);
        });

        for (const scriptElement of scriptElements)
        {
            const matches = scriptElement.className.match(/(?:^|\s)js-(\S+)/g);

            if (!matches)
            {
                continue;
            }

            for (const match of matches)
            {
                const name = '$' + this._dashToCamelCase(match.replace(/^\s*js-/, ''));

                if (typeof results[name] === 'undefined' || results[name] === null)
                {
                    results[name] = [scriptElement];
                }
                else
                {
                    results[name].push(scriptElement);
                }
            }
        }

        return results;
    },

    /**
     * Parses config attributes or JSON.
     *
     * @param {Object} result
     * @param {string} attributeValue
     * @param {jQuery} $module
     * @returns {Object}
     * @private
     */
    _parseConfig(result, attributeValue, $module)
    {
        'use strict';

        // looks like a JSON object
        if (attributeValue.startsWith('{'))
        {
            try
            {
                result = jQuery.parseJSON(attributeValue);
            } catch (e)
            {
                console.warn(e.message, attributeValue, $module);
            }
        }

        // key1: value1, key2: value2, ...
        else
        {
            // split by comma but accept escape sequence "\,"
            const pairs = attributeValue.match(/(\\.|[^,])+/g);

            if (!pairs)
            {
                return result;
            }

            for (let pair of pairs)
            {
                // split by colon but accept escape sequence "\:"
                pair = pair.match(/^\s*((?:\\.|[^:])+?)\s*(?::\s*(.+?))?\s*$/);

                // replace escape sequence
                const key = pair[1].replace('\\,', ',').replace('\\:', ':');

                let value = pair[2];

                if (value !== undefined)
                {
                    // replace escape sequence
                    value = value.replace('\\,', ',').replace('\\:', ':');
                }

                if (value === 'true')
                {
                    value = true;
                }
                else if (value === 'false')
                {
                    value = false;
                }
                else if (value === 'null')
                {
                    value = null;
                }
                else if (value === 'undefined')
                {
                    value = undefined;
                }
                else if (jQuery.isNumeric(value))
                {
                    value = Number(value);
                }

                result[key] = value;
            }
        }

        return result;
    },

    /**
     * Initialize all relations like parents, children and siblings
     *
     * @param {Object} module
     * @private
     */
    _initRelations(module)
    {
        'use strict';

        /* eslint max-depth: off */

        /**
         * Parent modules
         *
         * @type {Object[]}
         */
        module['parents'] = [];

        /**
         * Child modules
         *
         * @type {Object[]}
         */
        module['children'] = [];

        /**
         * Sibling modules
         *
         * @type {Object[]}
         */
        module['siblings'] = [];

        this._initRelationHelpers(module);

        let $parent = module.$module.parent();

        // walk through all parents until we find a module
        while ($parent.length)
        {
            // this parent has a module
            if ($parent.data('has_module'))
            {
                const parentModules = $parent.data('modules');

                // add all parents
                module['parents'] = parentModules;

                // walk through each parent and add children and siblings
                for (const parent of parentModules)
                {
                    if (!parent)
                    {
                        continue;
                    }

                    parent['children'].push(module);

                    for (const child1 of parent['children'])
                    {
                        if (!child1)
                        {
                            continue;
                        }

                        for (const child2 of parent['children'])
                        {
                            if (!child2)
                            {
                                continue;
                            }

                            if (child1 === child2)
                            {
                                continue;
                            }

                            if (child1['siblings'].indexOf(child2) !== -1)
                            {
                                continue;
                            }

                            if (child1.$module.is(child2.$module))
                            {
                                continue;
                            }

                            child1['siblings'].push(child2);
                        }
                    }
                }

                break;
            }

            // otherwise try next parent
            $parent = $parent.parent();
        }
    },

    /**
     * Initialize relation helpers like parentOfType(), childrenOfType() and siblingsOfType()
     *
     * @param {Object} module
     * @private
     */
    _initRelationHelpers(module)
    {
        'use strict';

        /**
         * Find the parent of specified type
         *
         * @param {string} type
         * @returns {null|Object}
         */
        module['parentOfType'] = (type) =>
        {
            for (const parent of module['parents'])
            {
                if (!parent)
                {
                    continue;
                }

                if (parent.name === type)
                {
                    return parent;
                }
            }

            return null;
        };

        /**
         * Finds all children of specified type
         *
         * @param {string} type
         * @returns {Object[]}
         */
        module['childrenOfType'] = (type) =>
        {
            const result = [];

            for (const child of module['children'])
            {
                if (!child)
                {
                    continue;
                }

                if (child.name === type)
                {
                    result.push(child);
                }
            }

            return result;
        };

        /**
         * Finds all siblings of specified type
         *
         * @param {string} type
         * @returns {Object[]}
         */
        module['siblingsOfType'] = (type) =>
        {
            const result = [];

            for (const sibling of module['siblings'])
            {
                if (!sibling)
                {
                    continue;
                }

                if (sibling.name === type)
                {
                    result.push(sibling);
                }
            }

            return result;
        };
    },

    /**
     * Remove all relations to a module.
     *
     * @param {Object} module
     * @private
     */
    _removeRelations(module)
    {
        for (const instance of this.instances)
        {
            if (!instance)
            {
                continue;
            }

            instance['parents'] = instance['parents'].filter((parentInstance) =>
            {
                return parentInstance !== module;
            });

            instance['children'] = instance['children'].filter((childInstance) =>
            {
                return childInstance !== module;
            });

            instance['siblings'] = instance['siblings'].filter((siblingInstance) =>
            {
                return siblingInstance !== module;
            });
        }
    },

    /**
     * Notifies all modules about one or more module changes
     *
     * @param {Object[]} loadedModules
     * @param {Object[]} destroyedModules
     * @private
     */
    _emitUpdate(loadedModules, destroyedModules)
    {
        for (const module of this.instances)
        {
            if (!module)
            {
                continue;
            }

            // this module does not care about updates
            if (typeof module.update !== 'function')
            {
                continue;
            }

            // filter out self
            const
                addedModules = loadedModules.filter((m) =>
                {
                    return m !== module;
                }),
                removedModules = destroyedModules.filter((m) =>
                {
                    return m !== module;
                });

            if (addedModules.length || removedModules.length)
            {
                module.update(addedModules, removedModules);
            }
        }
    },
};

/**
 * Returns a module instance.
 *
 * @param {string} name
 * @returns {*}
 */
jQuery.fn['module'] = function (name)
{
    const instance = this.data('module_' + name);

    if (instance)
    {
        return instance;
    }
};

/**
 * Allow options override
 */
if (typeof window.modules === 'object')
{
    jQuery.extend(true, S.modules, window.modules);
}

jQuery(() =>
{
    if (S.modules.autoObserve && (S.modules.autoInit || S.modules.autoDestroy))
    {
        S.modules._initObserver();
    }

    if (S.modules.autoInit)
    {
        if (S.modules.lazyload)
        {
            S.modules.initLazy();
        }
        else
        {
            S.modules.init();
        }
    }

    if (S.modules.lazyload)
    {
        try
        {
            window.onbeforeprint = () =>
            {
                S.modules.init();
            };

            S.mq('print').addListener((mq) =>
            {
                if (mq.matches)
                {
                    S.modules.init();
                }
            });
        } catch (e)
        {
        }
    }
});
